/**
 * RetailerWholesalerProductPage.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Dedicated product-details page for retailer/wholesaler storefront products.
 *
 * Route:  /shop/<vendorSlug--vendorId>/product/<productSlug--productId>
 *         (registered ABOVE the non-exact /shop/:vendorId route in App.jsx)
 *
 * The quick-view modal (RetailerWholesalerProductModal) stays as-is; this page
 * gives the product room to breathe: the shared hover-zoom gallery, full
 * description, quantity + add-to-cart, reviews, and a related-products rail
 * (same vendor, same section/category).
 *
 * Data strategy: products only exist inside the vendor's `sections` tree
 * (there is no single-product endpoint), so we
 *   1. render instantly from `location.state.product` when navigated in-app,
 *   2. always ensure the vendor is (re)fetched via fetchVendorDetails so a
 *      hard refresh / shared deep-link works, then resolve the product by id.
 *
 * Currency comes from the market registry (formatCurrency) — never hardcoded.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation, Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import normalizeDescriptionHtml from "../utils/descriptionHtml";
import DealTag from "../components/DealTag";
import CrawlLink from "../components/CrawlLink";
import { productPathUnder, shopPath as storefrontPathFor } from "../utils/seoPaths";
import PriceCut from "../components/PriceCut";
import { hasSalePrice, unitPriceOf, unitSavingsOf } from "../utils/vendorDiscounts";
import { Tooltip, Badge } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShareIcon from "@mui/icons-material/Share";
import StorefrontIcon from "@mui/icons-material/Storefront";

import Navbar from "../components/Navbar";
/* Same stand-in the brand and healthcare product views use. A retailer may
   list a product before its photograph arrives, so the stage needs
   something to hold rather than nothing to render. */
import placeholderImage from "../assets/img/menuImage.png";
/* THE one product media viewer, shared with the brand page, the legacy
   product page, the restaurant dialog and the healthcare dialog. This page
   used to hand-roll its own rail, lens, magnified pane and lightbox. */
import ProductImageGallery from "../components/ProductImageGallery";
import Reviews from "../components/Reviews/Reviews";
import ProductShareModal from "../components/ProductShare/ProductShareModal";
import { SEOHead, stripHtmlForSeo } from "../components/SEO/SEOHead";
import { withVendorCacheBuster } from "../components/SEO/vendorSeo";
import {
  fetchVendorDetails,
} from "../redux/wholesaleRetailVendorSlice";
import {
  addToCart,
  removeFromCart,
  setCurrentVendor,
} from "../redux/retailerWholesalerCartSlice";
import { viewItem, addToCart as trackAddToCart } from "../utils/analytics";
import { buildVendorParam, extractVendorId } from "../utils/vendorSlug";
import { formatCurrency, SITE_ORIGIN } from "../config/market";
import { userRequest } from "../api/requestMethods";

/* Max related products shown in the rail. */
const RELATED_LIMIT = 8;

const RetailerWholesalerProductPage = () => {
  const { vendorId: vendorParam, productId: productParam } = useParams();
  const vendorId = extractVendorId(vendorParam);
  /* Product URLs reuse the generic "<slug>--<id>" scheme. */
  const productId = extractVendorId(productParam);

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const vendor = useSelector((state) => state.wholesaleRetailVendor.vendor);
  const isFetching = useSelector(
    (state) => state.wholesaleRetailVendor.isFetching
  );
  const cart = useSelector(
    (state) => state.retailerWholesalerCart.carts[vendorId] || {}
  );
  const cartCount = Object.values(cart).reduce(
    (total, item) => total + item.quantity,
    0
  );

  /* ── Resolve the product (and its section, for related items) ── */
  const vendorLoaded = String(vendor?._id) === String(vendorId);
  const { product: resolvedProduct, section } = useMemo(() => {
    if (vendorLoaded) {
      for (const s of vendor?.sections || []) {
        const p = (s.products || []).find(
          (item) => String(item._id) === String(productId)
        );
        if (p) return { product: p, section: s };
      }
    }
    return { product: null, section: null };
  }, [vendor, vendorLoaded, productId]);

  /* Dedicated single-product endpoint: small, fresh, country-scoped payload
     (GET /rw/rw-product/:id). It wins over the vendor-tree copy; the tree
     remains the fallback (and still powers the related-products rail). */
  const [endpointProduct, setEndpointProduct] = useState(null);
  useEffect(() => {
    let active = true;
    setEndpointProduct(null);
    userRequest
      .get(`/rw/rw-product/${productId}`)
      .then((res) => {
        if (active && res?.data?._id) setEndpointProduct(res.data);
      })
      .catch(() => {
        /* Fall back silently to the vendor-tree copy. */
      });
    return () => {
      active = false;
    };
  }, [productId]);

  /* Instant paint when navigated in-app; freshest server copy wins. */
  const product =
    endpointProduct || resolvedProduct || location.state?.product || null;
  const cartItem = (product && cart[product._id]) || {};

  /* ── Data bootstrapping ── */
  useEffect(() => {
    if (!vendorLoaded) {
      dispatch(fetchVendorDetails(vendorId));
    }
    dispatch(setCurrentVendor(vendorId));
  }, [dispatch, vendorId, vendorLoaded]);

  /* Analytics + scroll restore per product. */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    if (product?._id) {
      viewItem({ id: product._id, name: product.name, price: product.price });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, product?._id]);

  /* ── Media ─────────────────────────────────────────────────────────
     Cover image first, then the extra angles. Clips are handed to the
     gallery exactly as the product stored them: it accepts both the
     contract objects and the bare Cloudinary URL strings older rows carry,
     so the hand-written mapping that used to live here is gone, along with
     the hover lens, the magnified pane and the lightbox. All four were a
     third copy of the brand page's gallery. */
  const galleryImages = useMemo(() => {
    if (!product) return [];
    const cover =
      typeof product.image === "string" && product.image
        ? [product.image]
        : Array.isArray(product.images)
        ? product.images
        : [];
    return [
      ...cover,
      ...(Array.isArray(product.additionalImages)
        ? product.additionalImages
        : []),
    ];
  }, [product]);

  /* The magnified pane runs from the right edge of the stage to the right
     edge of the info column, so it fills the copy rather than a guessed
     width. Same arrangement as the brand product page. */
  const infoColRef = useRef(null);

  /* ── Related products: same vendor, same section/category ── */
  const relatedProducts = useMemo(() => {
    if (!section || !product) return [];
    return (section.products || [])
      .filter(
        (p) => p.enabled && String(p._id) !== String(product._id)
      )
      .slice(0, RELATED_LIMIT);
  }, [section, product]);

  const shopPath = `/shop/${buildVendorParam(vendor?.name, vendorId)}`;

  const goToProduct = (p) => {
    history.push({
      pathname: productPathUnder(shopPath, p),
      state: { product: p },
    });
  };

  /* ── Cart actions (same slice + semantics as the quick-view modal) ── */
  const handleAddToCart = () => {
    if (!product) return;
    dispatch(addToCart({ vendorId, product, quantity: 1 }));
    trackAddToCart({
      id: product._id,
      name: product.name,
      price: unitPriceOf(product),
      quantity: 1,
    });
    toast.success(`${product.name} added to cart`);
  };

  const handleIncrease = () => {
    dispatch(addToCart({ vendorId, product, quantity: 1 }));
    trackAddToCart({
      id: product._id,
      name: product.name,
      price: unitPriceOf(product),
      quantity: 1,
    });
  };

  const handleDecrease = () => {
    dispatch(addToCart({ vendorId, product, quantity: -1 }));
    if (cartItem.quantity === 1) {
      dispatch(removeFromCart({ vendorId, productId: product._id }));
    }
  };

  /* ── Share (branded poster modal, same as the storefront cards) ── */
  const [shareOpen, setShareOpen] = useState(false);
  const productUrl = `${SITE_ORIGIN}${shopPath}/product/${buildVendorParam(
    product?.name,
    productId
  )}`;
  const shareUrl = vendor ? withVendorCacheBuster(productUrl, vendor) : productUrl;

  /* ── Render states ── */
  const notFound = vendorLoaded && !product;

  if (!product && (isFetching || !vendorLoaded)) {
    return (
      <PageContainer>
        <Navbar bgColor="#2d3e50" />
        <SkeletonLayout>
          <SkeletonGallery />
          <SkeletonInfo>
            <SkeletonLine width="70%" height="28px" />
            <SkeletonLine width="30%" height="22px" />
            <SkeletonLine width="100%" height="14px" />
            <SkeletonLine width="100%" height="14px" />
            <SkeletonLine width="60%" height="14px" />
            <SkeletonLine width="45%" height="48px" />
          </SkeletonInfo>
        </SkeletonLayout>
      </PageContainer>
    );
  }

  if (notFound || !product) {
    return (
      <PageContainer>
        <Navbar bgColor="#2d3e50" />
        <NotFoundBox>
          <StorefrontIcon style={{ fontSize: 48, opacity: 0.4 }} />
          <h2>Product not found</h2>
          <p>
            This product may no longer be available from{" "}
            {vendor?.name || "this vendor"}.
          </p>
          <BackToShopButton to={shopPath}>
            <ArrowBackIcon style={{ fontSize: 18 }} />
            Back to {vendor?.name || "shop"}
          </BackToShopButton>
        </NotFoundBox>
      </PageContainer>
    );
  }

  const descriptionText = stripHtmlForSeo(product.description || "");

  return (
    <PageContainer>
      <SEOHead
        title={product.name}
        description={
          descriptionText
            ? descriptionText.slice(0, 160)
            : `Buy ${product.name} from ${vendor?.name || "Lusuku"}.`
        }
        imageUrl={product.image}
        /* The store id, whichever id the link carried (owner ids reach this
           page and it does not rewrite them), so it matches the sitemap. */
        canonicalPath={productPathUnder(
          storefrontPathFor("retail", vendor) || shopPath,
          { _id: productId, name: product.name },
        )}
        type="product"
        product={{
          _id: product._id,
          name: product.name,
          description: descriptionText,
          imageUrl: product.image,
          price: product.price,
          vendorName: vendor?.name,
          available: product.enabled !== false,
        }}
      />
      <Navbar bgColor="#2d3e50" />

      {/* Breadcrumb / back to shop */}
      <Breadcrumb>
        <BackToShopButton to={shopPath}>
          <ArrowBackIcon style={{ fontSize: 18 }} />
          {vendor?.name || "Back to shop"}
        </BackToShopButton>
        {section?.title && <BreadcrumbCategory>{section.title}</BreadcrumbCategory>}
      </Breadcrumb>

      <MainLayout>
        {/* ── Gallery column ── */}
        <GalleryColumn>
          {/* Remounted per product so a jump to a related item starts on
              that item's cover rather than the angle that happened to be
              selected. */}
          <ProductImageGallery
            key={productId}
            images={galleryImages}
            videos={product.videos}
            alt={product.name}
            placeholder={placeholderImage}
            detailRef={infoColRef}
            hoverZoom
            lightbox
            zoomScale={2.4}
            lensSize="42%"
            lensTint="rgba(242, 68, 5, 0.12)"
            accent="#f24405"
            mainHeight="480px"
            mobileMainHeight="340px"
            /* This page's own collapse points, not the platform default.
               MainLayout below drops to a single column at 900px, which is
               where the rail has to stop sitting beside the stage, and the
               magnified pane has no info column left to fill at 1024px. */
            railBreakpoint={900}
            zoomPanelBreakpoint={1024}
            /* A shop aisle is not a cinema. A clip here starts when the
               shopper presses play, the way this page has always behaved;
               brand and the legacy product page autoplay on purpose. */
            videoAutoplay={false}
            hint="Hover to zoom · Click to enlarge"
            touchHint="Tap to enlarge"
            overlay={
              hasSalePrice(product) ? (
                <DealTag
                  discount={product.discount}
                  size="l"
                  radius={12}
                  zIndex={2}
                />
              ) : null
            }
          />
        </GalleryColumn>

        {/* ── Info column ── */}
        <InfoColumn ref={infoColRef}>
          <ProductTitle>{product.name}</ProductTitle>
          <VendorLine>
            Sold by <Link to={shopPath}>{vendor?.name || "this vendor"}</Link>
          </VendorLine>

          <PriceRow>
            <PriceCut
              component={PriceValue}
              price={product.price}
              listPrice={product.listPrice}
              salePrice={product.salePrice}
              format={formatCurrency}
              savings={unitSavingsOf(product)}
              endsAt={product.discount?.endsAt}
            />
            {product.unit_name && <UnitBadge>{product.unit_name}</UnitBadge>}
          </PriceRow>

          <Divider />

          <QuantityRow>
            <QuantityLabel>Quantity</QuantityLabel>
            <QuantityControls>
              <QuantityButton
                onClick={handleDecrease}
                disabled={!cartItem.quantity}
                aria-label="Decrease quantity"
              >
                <RemoveIcon fontSize="small" />
              </QuantityButton>
              <QuantityDisplay>{cartItem.quantity || 0}</QuantityDisplay>
              <QuantityButton onClick={handleIncrease} aria-label="Increase quantity">
                <AddIcon fontSize="small" />
              </QuantityButton>
            </QuantityControls>
          </QuantityRow>

          <ActionRow>
            <AddToCartButton
              onClick={handleAddToCart}
              disabled={cartItem.quantity > 0}
              className={cartItem.quantity > 0 ? "in-cart" : ""}
            >
              {cartItem.quantity > 0 ? (
                <>
                  <CheckCircleIcon style={{ fontSize: 20 }} />
                  Item in Cart
                </>
              ) : (
                <>🛒 Add to Cart</>
              )}
            </AddToCartButton>
            <Tooltip title="Share product">
              <SharePageButton onClick={() => setShareOpen(true)} aria-label="Share product">
                <ShareIcon style={{ fontSize: 20 }} />
              </SharePageButton>
            </Tooltip>
          </ActionRow>
        </InfoColumn>
      </MainLayout>

      {/* ── About this product — full-width below the fold, so the long
             vendor description has room to breathe instead of being
             squeezed into the narrow info column on the right. ── */}
      {(product.descriptionHtml || descriptionText) && (
        <DescriptionBlock>
          <h3>About this product</h3>
          {product.descriptionHtml ? (
            /* Vendor-authored rich description (standardized Quill
               formats). normalizeDescriptionHtml heals legacy entity
               double-encoding and upgrades "• " paragraphs to real
               lists; sanitized server-side on write AND DOMPurified
               here at render — defence in depth. */
            <RichDescription
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  normalizeDescriptionHtml(product.descriptionHtml)
                ),
              }}
            />
          ) : (
            <DescriptionText>{descriptionText}</DescriptionText>
          )}
        </DescriptionBlock>
      )}

      {/* ── Related products (same vendor, same category) ── */}
      {relatedProducts.length > 0 && (
        <RelatedSection>
          <RelatedHeader>
            More from {section?.title || "this category"}
          </RelatedHeader>
          <RelatedRail>
            {relatedProducts.map((p) => (
              <RelatedCard key={p._id} onClick={() => goToProduct(p)}>
                <CrawlLink
                  to={productPathUnder(shopPath, p)}
                  onNavigate={() => goToProduct(p)}
                  style={{ display: "block" }}
                >
                  <RelatedImageBox>
                    <img src={p.image} alt={p.name} loading="lazy" />
                    {hasSalePrice(p) && (
                      <DealTag discount={p.discount} size="s" radius={8} />
                    )}
                  </RelatedImageBox>
                </CrawlLink>
                <RelatedName>
                  <CrawlLink
                    to={productPathUnder(shopPath, p)}
                    onNavigate={() => goToProduct(p)}
                  >
                    {p.name}
                  </CrawlLink>
                </RelatedName>
                <PriceCut
                  component={RelatedPrice}
                  price={p.price}
                  listPrice={p.listPrice}
                  salePrice={p.salePrice}
                  format={formatCurrency}
                />
              </RelatedCard>
            ))}
          </RelatedRail>
        </RelatedSection>
      )}

      {/* ── Reviews ── */}
      {product?._id && (
        <ReviewsWrapper>
          <Reviews
            subjectType="product_retailer"
            subjectId={product._id}
            title="Customer reviews"
          />
        </ReviewsWrapper>
      )}

      {/* Floating cart shortcut, consistent with the storefront page */}
      <Tooltip title="View Cart">
        <FloatingCartIcon onClick={() => history.push("/cart")}>
          <Badge badgeContent={cartCount} color="secondary">
            🛒
          </Badge>
        </FloatingCartIcon>
      </Tooltip>

      {/* Branded share poster modal */}
      <ProductShareModal
        visible={shareOpen}
        item={shareOpen ? { ...product, vendorName: vendor?.name } : null}
        shopUrl={shareUrl}
        onClose={() => setShareOpen(false)}
      />
    </PageContainer>
  );
};

export default RetailerWholesalerProductPage;

/* ═══════════════════════════ styles ═══════════════════════════ */

const PageContainer = styled.div`
  padding: 20px;
  /* Offset for the fixed standard Navbar (same as VendorShopPage). */
  padding-top: var(--lusuku-nav-h, 121px);
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 850px) {
    padding: var(--lusuku-nav-h, 132px) 10px 20px;
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 6px 0 18px;
`;

const BackToShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: #042940;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 20px;
  background: #eef3f6;
  border: 1px solid #dbe5ea;
  transition: all 0.2s ease;

  &:hover {
    background: #e0ebf1;
    transform: translateX(-2px);
  }
`;

const BreadcrumbCategory = styled.span`
  font-size: 13px;
  color: #888;

  &:before {
    content: "/";
    margin-right: 12px;
    color: #ccc;
  }
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  gap: 32px;
  align-items: start;
  background: white;
  border: 1px solid #eaeaea;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
    padding: 14px;
  }
`;

/* ── Gallery ── */

/* The rail-beside-stage layout, and its flip to stage-above-rail on a
   phone, belong to the shared gallery now. This is just the grid cell. */
const GalleryColumn = styled.div`
  min-width: 0;
`;

/* ── Info column ── */

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

const ProductTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
  font-family: "Poppins", sans-serif;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const VendorLine = styled.p`
  margin: 0;
  font-size: 13.5px;
  color: #777;

  a {
    color: #0b6aa9;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PriceValue = styled.span`
  font-size: 30px;
  font-weight: 800;
  color: #2d8a2d;
  letter-spacing: -0.4px;
  font-family: "Poppins", sans-serif;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const UnitBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #666;
  padding: 4px 12px;
  border-radius: 12px;
  background-color: #f5f5f5;
  border: 1px solid #e8e8e8;
  white-space: nowrap;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 4px 0;
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const QuantityLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa;
  padding: 6px 14px;
  border-radius: 50px;
  border: 1px solid #ececec;
`;

const QuantityButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #e8f4f8;
  color: #042940;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #042940;
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #042940;
  min-width: 30px;
  text-align: center;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AddToCartButton = styled.button`
  flex: 1;
  padding: 15px 24px;
  background: linear-gradient(135deg, #84b026 0%, #6a9020 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 5px 16px rgba(132, 176, 38, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: "Poppins", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(132, 176, 38, 0.45);
  }

  &.in-cart {
    background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
    cursor: default;
  }

  &:disabled {
    opacity: 0.85;
  }
`;

const SharePageButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1.5px solid #e0e0e0;
  background: #f8f8f8;
  color: #555;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: #eaf4d3;
    border-color: #84b026;
    color: #5a8a00;
    transform: scale(1.08);
  }
`;

const DescriptionBlock = styled.div`
  margin-top: 24px;
  padding: 24px 28px;
  background: linear-gradient(135deg, #f8f9fa 0%, #eef1f4 100%);
  border-radius: 16px;
  border-left: 4px solid #84b026;

  h3 {
    margin: 0 0 12px;
    font-size: 17px;
    font-weight: 700;
    color: #042940;
    font-family: "Poppins", sans-serif;
  }

  @media (max-width: 768px) {
    margin-top: 18px;
    padding: 16px 16px;
    border-radius: 12px;
  }
`;

const DescriptionText = styled.p`
  font-family: "Urbanist", "Inter", "Segoe UI", sans-serif;
  font-size: 13.5px;
  line-height: 1.65;
  color: #444;
  margin: 0;
  white-space: pre-line;
`;

/* Standardized rendering for the Quill-authored rich description — the ONLY
   description styles on the storefront, so every vendor's listing looks
   uniform. This is the CANONICAL side of the WYSIWYG contract: the dashboard
   editor (richText.js DESCRIPTION_TYPOGRAPHY_CSS) and the quick-view modal
   mirror these exact values — change them together. */
const RichDescription = styled.div`
  font-family: "Urbanist", "Inter", "Segoe UI", sans-serif;
  font-size: 13.5px;
  line-height: 1.65;
  color: #444;

  p {
    margin: 0 0 9px;
  }

  h3,
  h4 {
    color: #042940;
    font-weight: 700;
    margin: 13px 0 7px;
    font-family: "Poppins", "Urbanist", sans-serif;
  }

  h3 {
    font-size: 15px;
  }

  h4 {
    font-size: 13.5px;
  }

  ul,
  ol {
    margin: 0 0 9px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 6px;
    padding-left: 2px;
  }

  /* Pronounced, brand-colored bullet — the standard list look. */
  ul > li::marker {
    color: #84b026;
    font-size: 1.25em;
    font-weight: 700;
  }

  ol > li::marker {
    color: #84b026;
    font-weight: 700;
  }

  blockquote {
    border-left: 3px solid #84b026;
    margin: 10px 0;
    padding: 6px 14px;
    color: #555;
    background: rgba(132, 176, 38, 0.07);
    border-radius: 0 8px 8px 0;
  }

  a {
    color: #0b6aa9;
    text-decoration: underline;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;

/* ── Related rail ── */

const RelatedSection = styled.section`
  margin-top: 28px;
`;

const RelatedHeader = styled.h2`
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 14px;
  padding-left: 12px;
  border-left: 4px solid #f24405;
`;

const RelatedRail = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 190px;
  gap: 16px;
  overflow-x: auto;
  padding: 4px 4px 14px;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    grid-auto-columns: 150px;
    gap: 12px;
  }
`;

const RelatedCard = styled.div`
  background: white;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: #84b026;
  }
`;

const RelatedImageBox = styled.div`
  position: relative;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  background: #fafafa;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    height: 105px;
  }
`;

const RelatedName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const RelatedPrice = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #2d8a2d;
  margin-top: auto;
`;

/* ── Misc ── */

const ReviewsWrapper = styled.div`
  margin-top: 28px;
`;

const FloatingCartIcon = styled.div`
  position: fixed;
  bottom: 90px;
  right: 20px;
  font-size: 2rem;
  background-color: #00ff00;
  color: white;
  padding: 15px;
  border-radius: 75%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 40;

  &:hover {
    background-color: #02ff88;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding: 12px;
    bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    padding: 10px;
    bottom: 20px;
    right: 12px;
  }
`;

const NotFoundBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 45vh;
  text-align: center;
  color: #555;

  h2 {
    margin: 6px 0 0;
    color: #333;
  }

  p {
    margin: 0 0 14px;
    font-size: 14px;
  }
`;

/* ── Skeleton ── */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  gap: 32px;
  margin-top: 20px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SkeletonPulse = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  border-radius: 10px;
`;

const SkeletonGallery = styled(SkeletonPulse)`
  height: 480px;

  @media (max-width: 900px) {
    height: 320px;
  }
`;

const SkeletonInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SkeletonLine = styled(SkeletonPulse)`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "14px"};
`;
